import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CButton,
  CPagination,
  CPaginationItem,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPencil, cilLockLocked, cilWarning } from "@coreui/icons";

const UserTable = ({
  users,
  loading,
  pagination,
  fetchUsers,
  openModal,
  openStatsModal,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="text-center p-4">
        <CSpinner />
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <CTable align="middle" className="mb-0 border" hover responsive>
        <CTableHead color="light">
          <CTableRow>
            <CTableHeaderCell>{t("users.username")}</CTableHeaderCell>
            <CTableHeaderCell>{t("users.fullname")}</CTableHeaderCell>
            <CTableHeaderCell>{t("users.email")}</CTableHeaderCell>
            <CTableHeaderCell>{t("users.role")}</CTableHeaderCell>
            <CTableHeaderCell>{t("users.actions")}</CTableHeaderCell>
            <CTableHeaderCell>{t("users.markup")}</CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {users.map((user) => (
            <CTableRow key={user.id}>
              <CTableDataCell>{user.username}</CTableDataCell>
              <CTableDataCell>{`${user.first_name} ${user.last_name}`}</CTableDataCell>
              <CTableDataCell>{user.email}</CTableDataCell>
              <CTableDataCell>
                {user.is_superuser
                  ? t("users.role_superuser")
                  : user.is_staff
                    ? t("users.role_admin")
                    : t("users.role_user")}
              </CTableDataCell>
              <CTableDataCell>
                {user.is_superuser ? (
                  <span className="text-muted fst-italic">
                    <CIcon icon={cilWarning} className="me-1" />
                    {t("users.unavailable")}
                  </span>
                ) : (
                  <>
                    <CButton
                      color="secondary"
                      size="sm"
                      className="me-2"
                      onClick={() => openModal("edit", user)}
                    >
                      <CIcon icon={cilPencil} />
                    </CButton>
                    <CButton
                      color="warning"
                      size="sm"
                      className="me-2"
                      onClick={() => openModal("password", user)}
                    >
                      <CIcon icon={cilLockLocked} />
                    </CButton>
                    {user.can_do_markup && (
                      <CButton
                        color="info"
                        size="sm"
                        className="text-white"
                        onClick={() => openStatsModal(user)}
                        title="Статистика"
                      >
                        📊
                      </CButton>
                    )}
                  </>
                )}
              </CTableDataCell>
              <CTableDataCell>
                {user.can_do_markup ? (
                  <span className="text-success fw-bold">Да</span>
                ) : (
                  <span className="text-muted">Нет</span>
                )}
              </CTableDataCell>
            </CTableRow>
          ))}
        </CTableBody>
      </CTable>

      {pagination.totalPages > 1 && (
        <CPagination className="mt-3 justify-content-center">
          {[...Array(pagination.totalPages).keys()].map((num) => (
            <CPaginationItem
              key={num}
              active={pagination.currentPage === num + 1}
              onClick={() => fetchUsers(num + 1)}
            >
              {num + 1}
            </CPaginationItem>
          ))}
        </CPagination>
      )}
    </div>
  );
};

UserTable.propTypes = {
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired,
  pagination: PropTypes.shape({
    currentPage: PropTypes.number,
    totalPages: PropTypes.number,
  }).isRequired,
  fetchUsers: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  openStatsModal: PropTypes.func.isRequired,
};

export default UserTable;